import React, { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../state/hooks';
import {
  fetchPendingActions,
  fetchRecentActions,
  fetchAuditLog,
  fetchAutonomyProfile,
  approveActionAsync,
  rejectActionAsync,
} from '../../state/slices/agencySlice';
import { HelpTrigger } from '../help/HelpPanel';
import { useIsSampleCompany, SAMPLE_COMPANY_TOOLTIP } from '../../state/hooks/useIsSampleCompany';
import './AgencyConsoleTab.css';

const AgencyConsoleTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const { activeCompanyId } = useAppSelector((state) => state.companies);
  const companyId = activeCompanyId ? parseInt(activeCompanyId.replace('company_', '')) : null;
  const { pendingActions, recentActions, auditLog, autonomyProfile, loading } =
    useAppSelector((state) => state.agency);
  const [activeSection, setActiveSection] = useState<'pending' | 'recent' | 'audit' | 'profile'>('pending');
  const isSampleCompany = useIsSampleCompany();

  useEffect(() => {
    if (companyId) {
      dispatch(fetchPendingActions(companyId));
      dispatch(fetchRecentActions(companyId));
      dispatch(fetchAuditLog(companyId));
      dispatch(fetchAutonomyProfile(companyId));
    }
  }, [companyId, dispatch]);

  const handleApprove = (actionId: number) => {
    dispatch(approveActionAsync(actionId)).then(() => {
      if (companyId) dispatch(fetchRecentActions(companyId));
    });
  };

  const handleReject = (actionId: number) => {
    dispatch(rejectActionAsync(actionId)).then(() => {
      if (companyId) dispatch(fetchRecentActions(companyId));
    });
  };

  if (!companyId) {
    return <div className="agency-console-empty">Select a company to view Agency Console.</div>;
  }

  return (
    <div className="agency-console">
      <div className="agency-nav">
        <button className={activeSection === 'pending' ? 'active' : ''} onClick={() => setActiveSection('pending')}>
          Pending ({pendingActions.length})
        </button>
        <button className={activeSection === 'recent' ? 'active' : ''} onClick={() => setActiveSection('recent')}>
          Recent
        </button>
        <button className={activeSection === 'audit' ? 'active' : ''} onClick={() => setActiveSection('audit')}>
          Audit Log
        </button>
        <button className={activeSection === 'profile' ? 'active' : ''} onClick={() => setActiveSection('profile')}>
          Autonomy Profile
        </button>
        <HelpTrigger section={`agency-${activeSection}`} />
      </div>

      {loading && <div className="agency-loading">Loading...</div>}

      {activeSection === 'pending' && (
        <div className="agency-section">
          <h3>Pending Actions</h3>
          {pendingActions.length === 0 && <p className="agency-empty">No pending actions.</p>}
          {pendingActions.map((action) => (
            <div key={action.id} className={`agency-action-card severity-${action.domain}`}>
              <div className="action-header">
                <span className="action-type">{action.action_type}</span>
                <span className={`action-status status-${action.status}`}>{action.status}</span>
              </div>
              {action.rationale && <p className="action-rationale">{action.rationale}</p>}
              <div className="action-meta">
                <span>Domain: {action.domain}</span>
                {action.estimated_impact && <span>Impact: ${action.estimated_impact.amount}</span>}
                <span>{new Date(action.created_at).toLocaleString()}</span>
              </div>
              {action.status === 'pending_approval' && (
                <div className="action-buttons">
                  <button
                    className="btn-approve"
                    onClick={() => handleApprove(action.id)}
                    disabled={isSampleCompany}
                    title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : undefined}
                  >
                    Approve
                  </button>
                  <button
                    className="btn-reject"
                    onClick={() => handleReject(action.id)}
                    disabled={isSampleCompany}
                    title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : undefined}
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {activeSection === 'recent' && (
        <div className="agency-section">
          <h3>Recent Actions</h3>
          {recentActions.map((action) => (
            <div key={action.id} className={`agency-action-card status-${action.status}`}>
              <div className="action-header">
                <span className="action-type">{action.action_type}</span>
                <span className={`action-status status-${action.status}`}>{action.status}</span>
              </div>
              <div className="action-meta">
                <span>{action.domain}</span>
                <span>{new Date(action.created_at).toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeSection === 'audit' && (
        <div className="agency-section">
          <h3>Audit Log</h3>
          <div className="audit-table">
            {auditLog.map((entry) => (
              <div key={entry.id} className="audit-entry">
                <span className="audit-event">{entry.event_type}</span>
                <span className="audit-domain">{entry.domain}</span>
                <span className="audit-time">{new Date(entry.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeSection === 'profile' && (
        <div className="agency-section">
          <h3>Autonomy Profile</h3>
          {autonomyProfile ? (
            <pre className="profile-json">{JSON.stringify(autonomyProfile, null, 2)}</pre>
          ) : (
            <p className="agency-empty">No autonomy profile configured.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default AgencyConsoleTab;
